import {BUBBLE, Component, DONE, isInside} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {Rect} from "@/event/calendar/Shapes.ts";
import {SPLITTER_WIDTH} from "@/event/calendar/Constants.ts";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {dc} from "@fd/editors/html/plugins/Keymap.ts";
import {CustomEvent} from "@/event/calendar/dom/CustomEvent.ts";
import type {Animation} from "@/event/calendar/dom/Animation.ts";

export interface SplitOptions {
	horizontal?: boolean;	// Default split is vertical. If you need horizontal split set this to true
	min?: number;	// minimum size of primary component in percent (0..1)
	max?: number;	// maximum size of primary component in percent (0..1)
	initial?: number;	// initial size of primary component in percent (0..1). This is used if the splitter doesn't find saved value
	saveAs?: string;	// actual size is saved into session storage with this name
}

export class SplitComponent extends Component {
	private primary: Component;
	private secondary: Component;

	private horizontal:boolean;
	private hover: boolean;
	private ratio:number;
	private min: number;
	private max: number;
	private saveAs: string;
	private drag:boolean;
	private startPrimarySize: number;
	private startMousePosition: number;

	constructor(primary: Component, secondary:Component, ops:SplitOptions) {
		super();
		this.hover = false;
		this.drag = false;
		this.primary = primary;
		this.secondary = secondary;
		this.horizontal = !!ops.horizontal;
		this.min = typeof ops.min === "number" ? ops.min : 0.1;
		this.max = typeof ops.max === "number" ? ops.max : 0.9;
		this.saveAs = ops.saveAs || null;
		this.initRatio(ops.initial)
		this.append(this.primary).append(this.secondary);
	}

	setRatio(r: number) {
		if (r > this.max) r = this.max;
		if (r < this.min) r = this.min;
		if (typeof this.saveAs === "string") {
			sessionStorage.setItem(this.saveAs, r.toString());
		}
		this.ratio = r;
	}

	initRatio(initial?: number) {
		if (typeof this.saveAs === "string") {
			const saved = sessionStorage.getItem(this.saveAs);
			if (saved) {
				this.ratio = parseFloat(saved);
				if (this.ratio > this.max) this.ratio = this.max;
				if (this.ratio < this.min) this.ratio = this.min;
				return;
			}
		}
		if (typeof initial === "number") {
			this.setRatio(initial);
			return;
		}
		this.ratio = Math.min(this.max, 2 * this.min);
	}

	get primarySize():number {
		const box = this.calculateContentBox(this.width, this.height);
		const space = (this.horizontal ? box.width : box.height) - SPLITTER_WIDTH;	// distributable space
		return Math.round(space * this.ratio);
	}

	set primarySize(size:number) {
		const box = this.calculateContentBox(this.width, this.height);
		const space = (this.horizontal ? box.width : box.height) - SPLITTER_WIDTH;	// distributable space
		this.setRatio(size / space);
	}

	placeChildren(dc: DrawingContext, contentBox: Rect) {
		const ps = this.primarySize;
		if (this.horizontal) {
			this.primary.x = contentBox.x;
			this.primary.y = contentBox.y;
			this.primary.width = ps;
			this.primary.height = contentBox.height;

			this.secondary.x = contentBox.x + this.primary.width + SPLITTER_WIDTH;
			this.secondary.y = contentBox.y;
			this.secondary.width = contentBox.width - ps - SPLITTER_WIDTH;
			this.secondary.height = contentBox.height;

		} else { // vertical
			this.primary.x = contentBox.x;
			this.primary.y = contentBox.y;
			this.primary.width = contentBox.width;
			this.primary.height = ps;

			this.secondary.x = contentBox.x;
			this.secondary.y = contentBox.y + this.primary.height + SPLITTER_WIDTH;
			this.secondary.width = contentBox.width;
			this.secondary.height = contentBox.height - ps - SPLITTER_WIDTH;
		}
	}

	splitterRect():Rect {
		const contentBox = this.calculateContentBox(this.width, this.height);
		if (this.horizontal) {
			return { x: contentBox.x + this.primarySize, y: contentBox.y, width: SPLITTER_WIDTH, height: contentBox.height };
		} else {
			return { x: contentBox.x, y: contentBox.y + this.primarySize, width: contentBox.width, height: SPLITTER_WIDTH };
		}
	}


	protected drawBackground(dc: DrawingContext, area: Rect) {
		const separatorColor = dc.style.getPropertyValue("--cal-header-separ");
		dc.ctx.fillStyle = this.hover ? "#FF9900" : separatorColor;
		const r = this.splitterRect();
		dc.ctx.fillRect(r.x, r.y, r.width, r.height);
	}


	getCursor(x: number, y: number): string {
		const inside = isInside(x, y, this.splitterRect());
		if (inside) {
			return this.horizontal ? "col-resize" : "row-resize";
		}
	}

	onPointerMove(e: PointerEvent, localX: number, localY: number) {
		if (this.drag) {
			//console.log("Dragging "+localX+", "+localY);
			const mousePosition = this.horizontal ? localX : localY;
			const diff = mousePosition - this.startMousePosition;
			if (diff !== 0) {
				this.primarySize = this.startPrimarySize + diff;
				this.dirty(true);
			}
			return false;
		} else {
			//console.log("Not dragging "+localX+", "+localY);
			const inside = isInside(localX, localY, this.splitterRect());
			const oldHover = this.hover;
			this.hover = inside;
			if (oldHover !== this.hover) this.dirty(false);
			return true;
		}
	}


	/* Reset cursor */
	onPointerLeave(e: PointerEvent, x: number, y: number) {
		super.onPointerLeave(e, x, y);
		const oldHover = this.hover;
		this.hover = false;
		this.fireEvent("cursor", { value : null });
		if (oldHover) this.dirty(false);
		return true;
	}

	// Resizing start
	onPointerDown(e: PointerEvent, x: number, y: number) {
		if (isInside(x, y, this.splitterRect())) {
			e.preventDefault();
			this.drag = true;
			this.startPrimarySize = this.primarySize;
			this.startMousePosition = this.horizontal ? x : y;
			this.setPointerCapture(e);
			return false;
		}
		return true;
	}

	onPointerUp(e: PointerEvent, x: number, y: number): boolean {
		//console.log("Drag end " + x + ", " + y);
		if (this.drag) {
			this.drag = false;
			this.dirty(false);
		}
		return false;
	}

	onEvent(e: CustomEvent): boolean {
		if (e.name === "split") {
			this.primarySize = e.size;
			this.dirty(true);
			// const oldR = this.primarySize;
			// const dist = e.size - this.primarySize;
			//
			// this.animate({
			// 	animate: (t:number, a: Animation):boolean =>  {
			// 		this.primarySize = Math.round(oldR + t * dist);
			// 		this.dirty(true);
			// 		return false;
			// 	},
			// 	duration: 500,
			// 	easing: "ease-out"
			// })
			return DONE;
		}
		return BUBBLE;
	}
}